import { useEffect, useState } from "react";

import type {
  BoardGrouping,
  BoardOrdering,
} from "pages/approvals/board/components/display-menu";
import type { PostsView } from "components/Posts/posts-deep-link";
import { PAGES, type PageName } from "constants/Pages";

import {
  getPublishingOptions,
  sanitizePublishingStates,
} from "../posts-publishing-state";
import type { Stage } from "pages/approvals/board/components/post-stage";

const STORAGE_KEY = "sked.posts.shell.v1";
// Versionless entries were written before we tracked a schema version, so the
// first explicit persisted version starts at 2.
const CURRENT_STATE_VERSION = 5;

type PostsShellState = {
  view: PostsView;
  grouping: BoardGrouping;
  ordering: BoardOrdering;
  orderingByPage: Partial<Record<PageName, BoardOrdering>>;
  publishingStatesByPage: Partial<Record<PageName, Stage[]>>;
  selectedLabelIds: string[] | null;
  statusesCleared: boolean;
  typesCleared: boolean;
  collapsedGroupIds: string[];
};

const DEFAULT_STATE: PostsShellState = {
  view: "list",
  grouping: "none",
  ordering: "oldest-scheduled",
  orderingByPage: {},
  publishingStatesByPage: {},
  selectedLabelIds: null,
  statusesCleared: false,
  typesCleared: false,
  collapsedGroupIds: [],
};

const GROUPINGS = new Set<BoardGrouping>([
  "none",
  "content-pillar",
  "campaign",
]);
const ORDERINGS = new Set<BoardOrdering>([
  "newest-scheduled",
  "oldest-scheduled",
  "newest-modified",
  "oldest-modified",
  "newest-created",
  "oldest-created",
]);

const DRAFT_ORDERINGS = new Set<BoardOrdering>([
  "newest-modified",
  "oldest-modified",
  "newest-created",
  "oldest-created",
]);

const isOrderingAllowedForPage = (ordering: BoardOrdering, page: PageName) =>
  page !== PAGES.DRAFT || DRAFT_ORDERINGS.has(ordering);

const getDefaultOrdering = (page?: PageName): BoardOrdering => {
  if (page === PAGES.DRAFT) return "newest-modified";
  if (page === PAGES.ARCHIVE) return "newest-scheduled";
  return DEFAULT_STATE.ordering;
};

const getOrderingForPage = (
  state: PostsShellState,
  page?: PageName
): BoardOrdering => {
  if (!page) return state.ordering;

  const storedOrdering = state.orderingByPage[page];
  if (storedOrdering && isOrderingAllowedForPage(storedOrdering, page)) {
    return storedOrdering;
  }
  return getDefaultOrdering(page);
};

function isLegacyDefaultState(candidate: Record<string, unknown>) {
  return (
    // A versionless entry using the legacy defaults represents the accidental
    // descending Upcoming sort that we need to migrate back to soonest-first.
    // This intentionally favors restoring the prior default over preserving an
    // indistinguishable explicit "newest-scheduled" choice in that exact state.
    candidate.version == null &&
    (candidate.grouping == null || candidate.grouping === "none") &&
    (candidate.ordering == null || candidate.ordering === "newest-scheduled") &&
    candidate.selectedLabelIds == null &&
    (candidate.statusesCleared == null ||
      candidate.statusesCleared === false) &&
    (candidate.typesCleared == null || candidate.typesCleared === false)
  );
}

function parseState(value: string): PostsShellState {
  const parsed: unknown = JSON.parse(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return DEFAULT_STATE;
  }

  const candidate = parsed as Record<string, unknown>;
  const orderingByPage =
    candidate.orderingByPage &&
    typeof candidate.orderingByPage === "object" &&
    !Array.isArray(candidate.orderingByPage)
      ? (Object.fromEntries(
          Object.entries(candidate.orderingByPage).filter(([, ordering]) =>
            ORDERINGS.has(ordering as BoardOrdering)
          )
        ) as Partial<Record<PageName, BoardOrdering>>)
      : DEFAULT_STATE.orderingByPage;
  const normalizedState: PostsShellState = {
    view:
      candidate.view === "list" || candidate.view === "table"
        ? candidate.view
        : DEFAULT_STATE.view,
    grouping: GROUPINGS.has(candidate.grouping as BoardGrouping)
      ? (candidate.grouping as BoardGrouping)
      : DEFAULT_STATE.grouping,
    ordering: ORDERINGS.has(candidate.ordering as BoardOrdering)
      ? (candidate.ordering as BoardOrdering)
      : DEFAULT_STATE.ordering,
    orderingByPage,
    publishingStatesByPage: Object.fromEntries(
      [PAGES.UPCOMING, PAGES.ARCHIVE].map((page) => {
        const values = (
          candidate.publishingStatesByPage as Record<string, unknown> | null
        )?.[page];
        const selected = sanitizePublishingStates(values, page);
        return [
          page,
          !Array.isArray(values) ||
          (candidate.version !== CURRENT_STATE_VERSION && selected.length === 0)
            ? getPublishingOptions(page).map(({ value }) => value)
            : selected,
        ];
      })
    ),
    selectedLabelIds:
      candidate.selectedLabelIds === null ||
      (Array.isArray(candidate.selectedLabelIds) &&
        candidate.selectedLabelIds.every((id) => typeof id === "string"))
        ? (candidate.selectedLabelIds as string[] | null)
        : DEFAULT_STATE.selectedLabelIds,
    statusesCleared:
      typeof candidate.statusesCleared === "boolean"
        ? candidate.statusesCleared
        : DEFAULT_STATE.statusesCleared,
    typesCleared:
      typeof candidate.typesCleared === "boolean"
        ? candidate.typesCleared
        : DEFAULT_STATE.typesCleared,
    collapsedGroupIds:
      Array.isArray(candidate.collapsedGroupIds) &&
      candidate.collapsedGroupIds.every((id) => typeof id === "string")
        ? (candidate.collapsedGroupIds as string[])
        : DEFAULT_STATE.collapsedGroupIds,
  };

  return isLegacyDefaultState(candidate)
    ? { ...normalizedState, ordering: DEFAULT_STATE.ordering }
    : normalizedState;
}

function readState(): PostsShellState {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    return value ? parseState(value) : DEFAULT_STATE;
  } catch {
    return DEFAULT_STATE;
  }
}

export function getStoredPostsView(): PostsView {
  return readState().view;
}

export function usePostsSessionState(
  currentView: PostsView = "list",
  currentPage?: PageName
) {
  const [state, setState] = useState<PostsShellState>(() => ({
    ...readState(),
    view: currentView,
  }));

  useEffect(() => {
    setState((current) =>
      current.view === currentView ? current : { ...current, view: currentView }
    );
  }, [currentView]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ version: CURRENT_STATE_VERSION, ...state })
      );
    } catch {
      // sessionStorage may be unavailable in hardened browser contexts.
    }
  }, [state]);

  const {
    orderingByPage: _orderingByPage,
    publishingStatesByPage: _publishingStatesByPage,
    ...publicState
  } = state;

  return {
    ...publicState,
    ordering: getOrderingForPage(state, currentPage),
    publishingStates: currentPage
      ? (state.publishingStatesByPage[currentPage] ??
        getPublishingOptions(currentPage).map(({ value }) => value))
      : [],
    setPublishingStates: (values: string[]) =>
      setState((current) =>
        currentPage
          ? {
              ...current,
              publishingStatesByPage: {
                ...current.publishingStatesByPage,
                [currentPage]: sanitizePublishingStates(values, currentPage),
              },
            }
          : current
      ),
    setView: (view: PostsView) => setState((current) => ({ ...current, view })),
    setGrouping: (grouping: BoardGrouping) =>
      setState((current) => ({ ...current, grouping })),
    setOrdering: (ordering: BoardOrdering) =>
      setState((current) =>
        currentPage
          ? {
              ...current,
              orderingByPage: {
                ...current.orderingByPage,
                [currentPage]: ordering,
              },
            }
          : { ...current, ordering }
      ),
    setSelectedLabelIds: (selectedLabelIds: string[] | null) =>
      setState((current) => ({ ...current, selectedLabelIds })),
    setStatusesCleared: (statusesCleared: boolean) =>
      setState((current) => ({ ...current, statusesCleared })),
    setTypesCleared: (typesCleared: boolean) =>
      setState((current) => ({ ...current, typesCleared })),
    toggleGroupCollapsed: (groupId: string) =>
      setState((current) => ({
        ...current,
        collapsedGroupIds: current.collapsedGroupIds.includes(groupId)
          ? current.collapsedGroupIds.filter((id) => id !== groupId)
          : [...current.collapsedGroupIds, groupId],
      })),
    resetFilters: () =>
      setState((current) => ({
        ...current,
        publishingStatesByPage: currentPage
          ? {
              ...current.publishingStatesByPage,
              [currentPage]: getPublishingOptions(currentPage).map(
                ({ value }) => value
              ),
            }
          : current.publishingStatesByPage,
        selectedLabelIds: null,
        statusesCleared: false,
        typesCleared: false,
      })),
  };
}

export type PostsSessionState = ReturnType<typeof usePostsSessionState>;
